import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { systemPrompts, LANGUAGE_LABELS } from '@/api/systemPrompts';
import type { SystemPromptRow, SystemPromptTranslationRow } from '@/api/systemPrompts';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Play, Save, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

const LANGS: Array<'en' | 'he' | 'yi'> = ['en', 'he', 'yi'];

interface LangRowProps {
  prompt: SystemPromptRow;
  lang: 'en' | 'he' | 'yi';
  translation: SystemPromptTranslationRow | undefined;
}

function LangRow({ prompt, lang, translation }: LangRowProps) {
  const qc = useQueryClient();
  const [text, setText] = useState(translation?.tts_text ?? '');
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      systemPrompts.upsertTranslation(prompt.id, lang, { tts_text: text || null }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['systemPrompt', prompt.id] });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => systemPrompts.preview(prompt.id, lang, text || undefined),
    onSuccess: (data) => {
      setPreviewUrl(data.url);
      setTimeout(() => audioRef.current?.play(), 100);
    },
  });

  const isRtl = lang === 'he' || lang === 'yi';

  return (
    <div className="grid grid-cols-[80px_1fr_auto] gap-2 items-start py-2 border-b border-slate-50 last:border-0">
      <span className={cn(
        'text-xs font-semibold text-slate-500 pt-2',
        (lang === 'he' || lang === 'yi') && 'text-amber-600',
      )}>
        {LANGUAGE_LABELS[lang]}
        {lang !== 'en' && !translation?.tts_text && (
          <span className="block text-[10px] font-normal text-amber-400">needs translation</span>
        )}
      </span>

      <Textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        rows={2}
        dir={isRtl ? 'rtl' : 'ltr'}
        className="text-sm resize-none"
        placeholder={lang === 'en' ? 'Enter English text…' : `Enter ${LANGUAGE_LABELS[lang]} translation…`}
      />

      <div className="flex flex-col gap-1.5 pt-0.5">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={!text.trim() || previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
          title="Preview TTS"
        >
          {previewMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Play className="h-3.5 w-3.5" />}
        </Button>
        <Button
          size="sm"
          className="h-7 px-2"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          title="Save"
        >
          {saveMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Save className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {previewUrl && (
        <audio ref={audioRef} src={previewUrl} className="hidden" onEnded={() => setPreviewUrl(null)} />
      )}
    </div>
  );
}

interface Props {
  prompt: SystemPromptRow;
  translations: SystemPromptTranslationRow[];
}

export function PromptTranslationEditor({ prompt, translations }: Props) {
  const byLang = Object.fromEntries(translations.map((t) => [t.language, t]));

  return (
    <div className="space-y-0">
      {LANGS.map((lang) => (
        <LangRow
          key={lang}
          prompt={prompt}
          lang={lang}
          translation={byLang[lang]}
        />
      ))}
    </div>
  );
}
